/**
 * Remove os code points que a produção `Char` do XML 1.0 **proíbe**, mantendo
 * todo o resto intacto (acentos, emoji, tab, quebra de linha).
 *
 * **Por que isto existe:** XLSX é um pacote de XML. O ExcelJS escreve o texto
 * das células cru no `sharedStrings.xml` e higieniza apenas `U+0000` — os
 * demais code points proibidos passam e produzem um arquivo que o Excel
 * **recusa abrir** (ou "repara" descartando o conteúdo). Para o consultor isso
 * aparece como "planilha vazia", com toast de sucesso, porque o download em si
 * funcionou.
 *
 * Origem (produção, 2026-07-21): a etapa "Mais Sobre Você" tinha `U+FFFE` na
 * resposta de UM candidato entre 2706 — provavelmente um hífen colado de outro
 * editor que degradou na codificação. O arquivo saía com 800 KB de dados e
 * não abria. Um caractere basta para corromper a planilha inteira.
 *
 * Proibidos pela `Char` (`#x9 | #xA | #xD | [#x20-#xD7FF] | [#xE000-#xFFFD] |
 * [#x10000-#x10FFFF]`): controles C0 exceto tab/LF/CR, `U+FFFE`/`U+FFFF`, os
 * noncharacters `U+FDD0–U+FDEF` e surrogates **soltos**. Pares de surrogate
 * válidos (emoji) são preservados — a limpeza é cirúrgica, não agressiva.
 *
 * Aplicado a TODA string da planilha (cabeçalho e células), não só às
 * respostas: o título vem do survey e o nome/e-mail vêm de `core.user` — todos
 * carregam texto digitado por gente e têm a mesma exposição.
 */
export function sanitizeXmlText(value: string): string {
  if (!value) return value;
  let out = '';
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);

    // Surrogate ALTO: só sobrevive se formar par válido com o próximo.
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = i + 1 < value.length ? value.charCodeAt(i + 1) : -1;
      if (next >= 0xdc00 && next <= 0xdfff) {
        out += value.slice(i, i + 2);
        i += 1;
      }
      continue;
    }
    if (code >= 0xdc00 && code <= 0xdfff) continue; // surrogate BAIXO solto
    if (code === 0x09 || code === 0x0a || code === 0x0d) {
      out += value[i];
      continue;
    }
    if (code < 0x20) continue; // demais controles C0
    if (code === 0xfffe || code === 0xffff) continue;
    if (code >= 0xfdd0 && code <= 0xfdef) continue; // noncharacters do BMP

    out += value[i];
  }
  return out;
}
